import { useState } from "react";
import { ArrowUpRight } from "lucide-react";

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "samoan", label: "Samoan Designs" },
  { key: "tongan", label: "Tongan Designs" },
  { key: "cook", label: "Cook Islands Designs" },
  { key: "church", label: "Church Uniforms" },
  { key: "reunion", label: "Reunion Uniforms" },
];

const ITEMS = [
  {
    cat: "samoan",
    img: "https://images.unsplash.com/photo-1618354691373-d851c5c3a990?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    label: "Samoan Heritage Polo",
    alt: "Samoan Church Uniforms — custom siapo pattern Pacific Heritage Polo Shirt Auckland NZ",
    span: "lg:col-span-2 lg:row-span-2",
  },
  {
    cat: "church",
    img: "https://images.pexels.com/photos/8532616/pexels-photo-8532616.jpeg?auto=compress&cs=tinysrgb&w=1000",
    label: "Choir Uniform Set",
    alt: "Church Choir Uniforms — custom Pacific Church Polo Shirts for Sunday choir NZ",
  },
  {
    cat: "tongan",
    img: "https://images.unsplash.com/photo-1603668103905-3ef7c99d7f9f?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000",
    label: "Tongan Ministry",
    alt: "Tongan Church Uniforms — custom ministry polo with ngatu pattern Auckland NZ",
  },
  {
    cat: "cook",
    img: "https://images.unsplash.com/photo-1585071258252-369a36d89e30?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000",
    label: "Cook Islands Pattern",
    alt: "Cook Islands Church Uniforms — custom Tivaevae pattern polo shirts NZ",
    span: "lg:col-span-2",
  },
  {
    cat: "samoan",
    img: "https://images.unsplash.com/photo-1644175529498-694e121b808a?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000",
    label: "Samoan Reunion",
    alt: "Samoan Family Reunion Polo Shirts — custom aiga reunion uniforms NZ",
  },
  {
    cat: "reunion",
    img: "https://images.unsplash.com/photo-1725531140878-9c17d139fcab?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000",
    label: "Village Reunion",
    alt: "Village Reunion Uniforms — custom Pacific village reunion polo shirts Auckland",
  },
  {
    cat: "church",
    img: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000",
    label: "Conference Set",
    alt: "Church Conference Polo Shirts — Pacific church conference uniforms Auckland NZ",
  },
  {
    cat: "tongan",
    img: "https://images.pexels.com/photos/9799205/pexels-photo-9799205.jpeg?auto=compress&cs=tinysrgb&w=1200",
    label: "Tongan Community",
    alt: "Tongan Pacific Community Uniforms — custom church polo shirts NZ",
    span: "lg:col-span-2",
  },
];

export default function Gallery() {
  const [active, setActive] = useState("all");
  const filtered = active === "all" ? ITEMS : ITEMS.filter((i) => i.cat === active);

  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">
              Recent designs
            </p>
            <h2 className="mt-3 font-display uppercase text-4xl md:text-6xl tracking-tight leading-[0.95]">
              The <span className="text-teal-400">gallery</span>
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md md:text-right">
            A glimpse of past projects: Samoan, Tongan and Cook Islands designs
            for churches, choirs, conferences and reunions across NZ.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="gallery-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              data-testid={`filter-${c.key}`}
              onClick={() => setActive(c.key)}
              className={`px-4 py-2 text-xs uppercase tracking-wider font-bold border transition-colors ${
                active === c.key
                  ? "bg-teal-400 text-zinc-950 border-teal-400"
                  : "bg-transparent text-zinc-300 border-zinc-800 hover:border-teal-400"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 auto-rows-[180px] md:auto-rows-[260px] gap-3">
          {filtered.map((it, i) => (
            <a
              key={`${active}-${i}`}
              href="#contact"
              data-testid={`gallery-item-${i}`}
              className={`group relative overflow-hidden bg-zinc-900 border border-zinc-800 ${it.span || ""}`}
            >
              <img
                src={it.img}
                alt={it.alt || it.label}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a1530] via-[#0a1530]/30 to-transparent opacity-80 group-hover:opacity-95 transition-opacity" />
              <div className="absolute inset-0 flex items-end p-4 md:p-6">
                <div className="w-full flex items-end justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-gold">
                      {CATEGORIES.find((c) => c.key === it.cat)?.label || "Design"}
                    </div>
                    <div className="font-display uppercase text-base md:text-xl text-white mt-1">
                      {it.label}
                    </div>
                  </div>
                  <div className="w-10 h-10 bg-teal-400 text-zinc-950 grid place-items-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all">
                    <ArrowUpRight size={18} />
                  </div>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
